import React, { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";

// "Sign in with Google" via Google's own button. The Client ID comes from the API (it is public), so
// turning the feature on or off is a server setting; with no Client ID this renders nothing at all.

let configPromise = null;
const loadClientId = () => {
  configPromise ||= http.get("/auth/config").then((r) => r.data.google_client_id || null).catch(() => { configPromise = null; return null; });
  return configPromise;
};

let scriptPromise = null;
const loadGoogleScript = () => {
  scriptPromise ||= new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => { scriptPromise = null; reject(new Error("Google script blocked")); };
    document.head.appendChild(s);
  });
  return scriptPromise;
};

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.1 5.5c4.2-3.9 7.2-9.6 7.2-16.9z" />
    <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.4-4.6 2.3-8.8 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

/**
 * mode: "login" | "signup"   ready: false shows a disabled stand-in with `hint`   onCredential(jwt)
 */
export default function GoogleSignIn({ mode, ready = true, hint, onCredential, disabled = false }) {
  const [clientId, setClientId] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const box = useRef(null);
  const latest = useRef(onCredential);
  latest.current = onCredential;
  const label = mode === "signup" ? "Sign up with Google" : "Continue with Google";

  useEffect(() => { let alive = true; loadClientId().then((id) => alive && setClientId(id)); return () => { alive = false; }; }, []);

  useEffect(() => {
    if (!clientId || !ready || !box.current) return;
    let cancelled = false;
    loadGoogleScript().then((google) => {
      if (cancelled || !box.current) return;
      google.accounts.id.initialize({
        client_id: clientId, ux_mode: "popup", cancel_on_tap_outside: true, auto_select: false,
        callback: (res) => res?.credential && latest.current(res.credential),
      });
      box.current.innerHTML = "";
      google.accounts.id.renderButton(box.current, {
        type: "standard", theme: "outline", size: "large", shape: "pill", logo_alignment: "left",
        text: mode === "signup" ? "signup_with" : "continue_with",
        width: Math.min(400, Math.max(240, Math.round(box.current.getBoundingClientRect().width || 320))),
      });
    }).catch(() => !cancelled && setBlocked(true));
    return () => { cancelled = true; };
  }, [clientId, ready, mode]);

  if (!clientId) return null;
  return (
    <div className="mt-5" data-testid="google-signin">
      <div className="mb-4 flex items-center gap-3 text-xs text-slate-400" aria-hidden="true">
        <span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" />
      </div>
      {ready && !blocked ? (
        <div className={`flex justify-center ${disabled ? "pointer-events-none opacity-60" : ""}`}>
          <div ref={box} data-testid="google-button" className="min-h-[44px] w-full max-w-[400px]" />
        </div>
      ) : (
        <>
          <button type="button" disabled data-testid="google-button-disabled"
            className="flex w-full items-center justify-center gap-2.5 rounded-full border border-slate-200 bg-white py-2.5 text-[14px] font-semibold text-slate-400">
            <span className="opacity-50"><GoogleG /></span> {label}
          </button>
          <p className="mt-2 text-center text-xs text-slate-500" data-testid="google-hint">
            {blocked ? "Google sign-in is blocked on this network. Use your email and password instead." : hint}
          </p>
        </>
      )}
    </div>
  );
}
